<template>
  <section id="access-tokens" class="scroll-mt-20" aria-labelledby="access-tokens-heading">
    <div class="flex flex-wrap items-end justify-between gap-4">
      <div>
        <h2 id="access-tokens-heading" class="text-xl font-semibold text-gray-900 dark:text-white">
          Personal access tokens
        </h2>
        <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
          Give scripts, CI jobs and agents scoped, expiring access to your account.
        </p>
      </div>
      <button
        type="button"
        class="inline-flex items-center gap-x-1.5 rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:hover:bg-indigo-400 dark:focus-visible:outline-indigo-500 cursor-pointer"
        @click="openCreate"
      >
        <PlusIcon class="-ml-0.5 size-5" aria-hidden="true" />
        Create token
      </button>
    </div>

    <div
      class="mt-6 rounded-2xl border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900/60 shadow-xs"
    >
      <div
        class="flex gap-3 border-b border-gray-100 p-4 sm:px-6 text-sm text-gray-600 dark:border-white/10 dark:text-gray-400"
      >
        <CommandLineIcon
          class="size-5 shrink-0 text-gray-400 dark:text-gray-500"
          aria-hidden="true"
        />
        <p>
          Set
          <code class="font-mono text-gray-900 dark:text-gray-200">MANAGE_RUNNERS_TOKEN</code>
          to use a token for
          <code class="font-mono text-gray-900 dark:text-gray-200">manage-runners</code> CLI
          commands, for example in CI. For a permanent login, use
          <code class="font-mono text-gray-900 dark:text-gray-200">auth login --token-stdin</code>.
        </p>
      </div>

      <div v-if="loading" class="p-6 text-sm text-gray-500 dark:text-gray-400" role="status">
        Loading tokens…
      </div>

      <div v-else-if="listError" class="p-6" role="alert">
        <p class="text-sm text-red-600 dark:text-red-400">{{ listError }}</p>
        <button
          type="button"
          class="mt-3 text-sm font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 cursor-pointer"
          @click="loadTokens"
        >
          Try again
        </button>
      </div>

      <div v-else-if="tokens.length === 0" class="p-6 text-center">
        <KeyIcon class="mx-auto size-10 text-gray-300 dark:text-gray-600" aria-hidden="true" />
        <p class="mt-2 text-sm font-semibold text-gray-900 dark:text-white">No tokens yet</p>
        <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Tokens created here or by <code class="font-mono">manage-runners auth login</code> appear
          in this list.
        </p>
      </div>

      <ul
        v-else
        role="list"
        class="divide-y divide-gray-100 dark:divide-white/10"
        aria-label="Personal access tokens"
      >
        <li
          v-for="token in tokens"
          :key="token.selector"
          class="flex flex-col gap-3 p-4 sm:flex-row sm:items-start sm:justify-between sm:px-6"
          :data-selector="token.selector"
        >
          <div class="min-w-0 flex-1">
            <div class="flex flex-wrap items-center gap-2">
              <p
                class="truncate text-sm font-semibold"
                :class="
                  statusOf(token) === 'active'
                    ? 'text-gray-900 dark:text-white'
                    : 'text-gray-500 dark:text-gray-400'
                "
              >
                {{ token.name }}
              </p>
              <span
                class="inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium inset-ring"
                :class="STATUS_BADGE[statusOf(token)].classes"
              >
                {{ STATUS_BADGE[statusOf(token)].label }}
              </span>
            </div>
            <p class="mt-1 font-mono text-xs text-gray-500 dark:text-gray-400 break-all">
              {{ displayPrefix(token.selector) }}
            </p>
            <ul
              role="list"
              class="mt-2 flex flex-wrap gap-1.5"
              :aria-label="`Scopes of ${token.name}`"
            >
              <li
                v-for="scope in token.scopes"
                :key="scope"
                class="inline-flex items-center rounded-md px-1.5 py-0.5 font-mono text-xs inset-ring"
                :class="
                  scopeInfo(scope)?.mutation
                    ? 'bg-amber-50 text-amber-800 inset-ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-300 dark:inset-ring-amber-400/20'
                    : 'bg-gray-50 text-gray-600 inset-ring-gray-500/10 dark:bg-white/5 dark:text-gray-300 dark:inset-ring-white/10'
                "
                :title="scopeInfo(scope)?.description"
              >
                {{ scope }}
              </li>
            </ul>
            <dl
              class="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500 dark:text-gray-400"
            >
              <div class="flex gap-1">
                <dt>Created</dt>
                <dd>
                  <time :datetime="token.createdAt" :title="formatDateTime(token.createdAt)">{{
                    formatDate(token.createdAt)
                  }}</time>
                </dd>
              </div>
              <div v-if="token.revokedAt" class="flex gap-1">
                <dt>Revoked</dt>
                <dd>
                  <time :datetime="token.revokedAt" :title="formatDateTime(token.revokedAt)">{{
                    formatDate(token.revokedAt)
                  }}</time>
                </dd>
              </div>
              <div class="flex gap-1">
                <dt>{{ statusOf(token) === 'expired' ? 'Expired' : 'Expires' }}</dt>
                <dd>
                  <time :datetime="token.expiresAt" :title="formatDateTime(token.expiresAt)">{{
                    formatDate(token.expiresAt)
                  }}</time>
                </dd>
              </div>
              <div class="flex gap-1">
                <dt>Last used</dt>
                <dd>
                  <time
                    v-if="token.lastUsedAt"
                    :datetime="token.lastUsedAt"
                    :title="formatDateTime(token.lastUsedAt)"
                    >{{ formatDate(token.lastUsedAt) }}</time
                  >
                  <template v-else>Never</template>
                </dd>
              </div>
            </dl>
          </div>
          <button
            v-if="statusOf(token) === 'active'"
            type="button"
            class="self-start rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-red-600 shadow-xs inset-ring inset-ring-gray-300 hover:bg-red-50 dark:bg-white/10 dark:text-red-400 dark:inset-ring-white/10 dark:hover:bg-red-500/10 cursor-pointer"
            :aria-label="`Revoke ${token.name}`"
            @click="askRevoke(token)"
          >
            Revoke
          </button>
        </li>
      </ul>
    </div>

    <TransitionRoot as="template" :show="createOpen">
      <Dialog
        as="div"
        class="relative z-50"
        :initial-focus="nameInput"
        @close="onCreateDialogClose"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-500/75 transition-opacity dark:bg-gray-900/80" />
        </TransitionChild>

        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div
            class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
          >
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                class="relative w-full transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:max-w-2xl sm:p-6 dark:bg-gray-800 dark:outline dark:-outline-offset-1 dark:outline-white/10"
              >
                <div v-if="createdToken" data-testid="token-reveal">
                  <div class="sm:flex sm:items-start">
                    <div
                      class="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-green-100 sm:mx-0 sm:size-10 dark:bg-green-500/10"
                    >
                      <CheckIcon
                        class="size-6 text-green-600 dark:text-green-400"
                        aria-hidden="true"
                      />
                    </div>
                    <div class="mt-3 min-w-0 flex-1 text-center sm:mt-0 sm:ml-4 sm:text-left">
                      <DialogTitle
                        as="h3"
                        class="text-base font-semibold text-gray-900 dark:text-white"
                      >
                        Token “{{ createdToken.name }}” created
                      </DialogTitle>
                      <div
                        class="mt-3 flex gap-2 rounded-md bg-amber-50 p-3 text-left text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-200"
                        role="alert"
                      >
                        <ExclamationTriangleIcon
                          class="size-5 shrink-0 text-amber-500 dark:text-amber-400"
                          aria-hidden="true"
                        />
                        <p>
                          Copy this token now.
                          <strong>You won't be able to see it again.</strong> Store it in a secret
                          manager and treat it like a password.
                        </p>
                      </div>
                      <label for="created-token" class="sr-only">Personal access token</label>
                      <div class="mt-3 flex rounded-md">
                        <input
                          id="created-token"
                          :value="createdToken.token"
                          readonly
                          spellcheck="false"
                          autocomplete="off"
                          class="block min-w-0 grow rounded-l-md bg-gray-50 px-3 py-1.5 font-mono text-sm text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:focus:outline-indigo-500"
                          @focus="selectAll"
                        />
                        <button
                          ref="copyButton"
                          type="button"
                          class="flex shrink-0 items-center gap-x-1.5 rounded-r-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 outline-1 -outline-offset-1 outline-gray-300 hover:bg-gray-50 focus:relative focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 dark:bg-white/10 dark:text-white dark:outline-gray-700 dark:hover:bg-white/20 dark:focus:outline-indigo-500 cursor-pointer"
                          @click="copyToken"
                        >
                          <ClipboardDocumentCheckIcon
                            v-if="copied"
                            class="-ml-0.5 size-4 text-green-600 dark:text-green-400"
                            aria-hidden="true"
                          />
                          <ClipboardDocumentIcon
                            v-else
                            class="-ml-0.5 size-4 text-gray-400"
                            aria-hidden="true"
                          />
                          {{ copied ? 'Copied' : 'Copy' }}
                        </button>
                      </div>
                      <p class="mt-2 text-xs text-gray-500 dark:text-gray-400" aria-live="polite">
                        <template v-if="copyFailed"
                          >Copying failed. Select the token and copy it manually.</template
                        >
                        <template v-else>
                          Expires {{ formatDateTime(createdToken.expiresAt) }}. Set
                          <code class="font-mono">MANAGE_RUNNERS_TOKEN</code> to use it with the
                          CLI, or log in permanently with
                          <code class="font-mono">manage-runners auth login --token-stdin</code>.
                        </template>
                      </p>
                    </div>
                  </div>
                  <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="button"
                      class="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 sm:w-auto dark:bg-indigo-500 dark:hover:bg-indigo-400 cursor-pointer"
                      @click="closeCreate"
                    >
                      Done
                    </button>
                  </div>
                </div>

                <form v-else novalidate @submit.prevent="submitCreate">
                  <DialogTitle
                    as="h3"
                    class="text-base font-semibold text-gray-900 dark:text-white"
                  >
                    Create personal access token
                  </DialogTitle>
                  <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Grant only the scopes the token needs. The token acts as you within those
                    scopes.
                  </p>

                  <div class="mt-5 space-y-6">
                    <div>
                      <label
                        for="token-name"
                        class="block text-sm font-medium text-gray-900 dark:text-white"
                        >Name</label
                      >
                      <input
                        id="token-name"
                        ref="nameInput"
                        v-model="form.name"
                        type="text"
                        :maxlength="PAT_NAME_MAX_LENGTH"
                        required
                        autocomplete="off"
                        placeholder="e.g. CI deploy agent"
                        :aria-invalid="Boolean(nameError)"
                        :aria-describedby="nameError ? 'token-name-error' : undefined"
                        :disabled="submitting"
                        class="mt-1 block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 disabled:cursor-not-allowed disabled:bg-gray-50 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:placeholder:text-gray-500 dark:focus:outline-indigo-500"
                        :class="nameError ? 'outline-red-500 dark:outline-red-400' : ''"
                      />
                      <p
                        v-if="nameError"
                        id="token-name-error"
                        class="mt-1 text-sm text-red-600 dark:text-red-400"
                      >
                        {{ nameError }}
                      </p>
                    </div>

                    <fieldset :aria-describedby="scopesError ? 'token-scopes-error' : undefined">
                      <legend class="text-sm font-medium text-gray-900 dark:text-white">
                        Scopes
                      </legend>
                      <div class="mt-1 flex gap-3 text-sm">
                        <button
                          type="button"
                          class="font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 cursor-pointer"
                          :disabled="submitting"
                          @click="form.scopes = [...READ_ONLY_SCOPES]"
                        >
                          Select read-only
                        </button>
                        <button
                          type="button"
                          class="font-medium text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white cursor-pointer"
                          :disabled="submitting"
                          @click="form.scopes = []"
                        >
                          Clear
                        </button>
                      </div>
                      <div
                        class="mt-2 divide-y divide-gray-200 rounded-md border border-gray-200 dark:divide-white/10 dark:border-white/10"
                      >
                        <div
                          v-for="scope in PAT_SCOPES"
                          :key="scope.value"
                          class="relative flex gap-3 px-3 py-2.5"
                          :class="
                            scope.paidResources
                              ? 'bg-red-50/60 dark:bg-red-500/5'
                              : scope.mutation
                                ? 'bg-amber-50/60 dark:bg-amber-500/5'
                                : ''
                          "
                        >
                          <div class="flex h-6 shrink-0 items-center">
                            <input
                              :id="`scope-${scope.value}`"
                              v-model="form.scopes"
                              :value="scope.value"
                              type="checkbox"
                              :disabled="submitting"
                              :aria-describedby="`scope-${scope.value}-description`"
                              class="size-4 rounded-sm border-gray-300 text-indigo-600 focus:ring-indigo-600 dark:border-white/10 dark:bg-white/5 dark:checked:bg-indigo-500"
                            />
                          </div>
                          <div class="min-w-0 text-sm/6">
                            <label
                              :for="`scope-${scope.value}`"
                              class="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium text-gray-900 dark:text-white"
                            >
                              {{ scope.label }}
                              <code
                                class="font-mono text-xs font-normal text-gray-500 dark:text-gray-400"
                                >{{ scope.value }}</code
                              >
                              <span
                                v-if="scope.mutation"
                                class="inline-flex items-center rounded-md bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-800 inset-ring inset-ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-300 dark:inset-ring-amber-400/20"
                                >Changes data</span
                              >
                              <span
                                v-if="scope.paidResources"
                                class="inline-flex items-center rounded-md bg-red-50 px-1.5 py-0.5 text-xs font-medium text-red-700 inset-ring inset-ring-red-600/10 dark:bg-red-400/10 dark:text-red-400 dark:inset-ring-red-400/20"
                                >Creates paid resources</span
                              >
                              <span
                                v-if="scope.requiresPaidPlan"
                                class="inline-flex items-center rounded-md bg-indigo-50 px-1.5 py-0.5 text-xs font-medium text-indigo-700 inset-ring inset-ring-indigo-700/10 dark:bg-indigo-400/10 dark:text-indigo-300 dark:inset-ring-indigo-400/30"
                                >Pro plan</span
                              >
                            </label>
                            <p
                              :id="`scope-${scope.value}-description`"
                              class="text-gray-500 dark:text-gray-400"
                            >
                              {{ scope.description }}
                            </p>
                          </div>
                        </div>
                      </div>
                      <p
                        v-if="scopesError"
                        id="token-scopes-error"
                        class="mt-1 text-sm text-red-600 dark:text-red-400"
                      >
                        {{ scopesError }}
                      </p>
                    </fieldset>

                    <div>
                      <label
                        for="token-expiry"
                        class="block text-sm font-medium text-gray-900 dark:text-white"
                        >Expiry</label
                      >
                      <div class="mt-1 grid grid-cols-1 sm:max-w-xs">
                        <select
                          id="token-expiry"
                          v-model="form.expiry"
                          :disabled="submitting"
                          :aria-invalid="Boolean(expiryError)"
                          :aria-describedby="
                            expiryError
                              ? 'token-expiry-hint token-expiry-error'
                              : 'token-expiry-hint'
                          "
                          class="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-1.5 pr-8 pl-3 text-base text-gray-900 outline-1 -outline-offset-1 outline-gray-300 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-600 sm:text-sm/6 dark:bg-white/5 dark:text-white dark:outline-gray-700 dark:*:bg-gray-800 dark:focus:outline-indigo-500"
                        >
                          <option
                            v-for="option in EXPIRY_OPTIONS"
                            :key="option.id"
                            :value="option.id"
                          >
                            {{ option.label }}
                          </option>
                        </select>
                        <ChevronDownIcon
                          class="pointer-events-none col-start-1 row-start-1 mr-2 size-5 self-center justify-self-end text-gray-500 sm:size-4 dark:text-gray-400"
                          aria-hidden="true"
                        />
                      </div>
                      <p
                        id="token-expiry-hint"
                        class="mt-1 text-sm text-gray-500 dark:text-gray-400"
                      >
                        Expires on {{ formatDate(expiryPreview.toISOString()) }}. Tokens can't be
                        extended; create a new one before this date.
                      </p>
                      <p
                        v-if="expiryError"
                        id="token-expiry-error"
                        class="mt-1 text-sm text-red-600 dark:text-red-400"
                      >
                        {{ expiryError }}
                      </p>
                    </div>
                  </div>

                  <p
                    v-if="createError"
                    class="mt-4 text-sm text-red-600 dark:text-red-400"
                    role="alert"
                  >
                    {{ createError }}
                  </p>
                  <div v-if="createNeedsVerification" class="mt-3">
                    <EmailVerificationForm @verified="onEmailVerified" />
                  </div>

                  <div class="mt-6 sm:flex sm:flex-row-reverse gap-3">
                    <button
                      type="submit"
                      :disabled="submitting"
                      class="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 disabled:cursor-wait disabled:opacity-50 sm:w-auto dark:bg-indigo-500 dark:hover:bg-indigo-400 cursor-pointer"
                    >
                      {{ submitting ? 'Creating…' : 'Create token' }}
                    </button>
                    <button
                      type="button"
                      :disabled="submitting"
                      class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto dark:bg-white/10 dark:text-white dark:inset-ring-white/5 dark:hover:bg-white/20 cursor-pointer"
                      @click="closeCreate"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>

    <TransitionRoot as="template" :show="revokeTarget !== null">
      <Dialog
        as="div"
        class="relative z-50"
        :initial-focus="cancelRevokeButton"
        @close="cancelRevoke"
      >
        <TransitionChild
          as="template"
          enter="ease-out duration-300"
          enter-from="opacity-0"
          enter-to="opacity-100"
          leave="ease-in duration-200"
          leave-from="opacity-100"
          leave-to="opacity-0"
        >
          <div class="fixed inset-0 bg-gray-500/75 transition-opacity dark:bg-gray-900/80" />
        </TransitionChild>

        <div class="fixed inset-0 z-10 overflow-y-auto">
          <div
            class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0"
          >
            <TransitionChild
              as="template"
              enter="ease-out duration-300"
              enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enter-to="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leave-from="opacity-100 translate-y-0 sm:scale-100"
              leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <DialogPanel
                class="relative transform overflow-hidden rounded-lg bg-white px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6 dark:bg-gray-800 dark:outline dark:-outline-offset-1 dark:outline-white/10"
              >
                <div class="sm:flex sm:items-start">
                  <div
                    class="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10 dark:bg-red-500/10"
                  >
                    <ExclamationTriangleIcon
                      class="size-6 text-red-600 dark:text-red-400"
                      aria-hidden="true"
                    />
                  </div>
                  <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <DialogTitle
                      as="h3"
                      class="text-base font-semibold text-gray-900 dark:text-white"
                    >
                      Revoke token “{{ revokeTarget?.name }}”?
                    </DialogTitle>
                    <p class="mt-2 text-sm text-gray-500 dark:text-gray-400">
                      Scripts, CI jobs and CLI profiles using this token stop working immediately.
                      This can't be undone.
                    </p>
                    <p
                      v-if="revokeError"
                      class="mt-2 text-sm text-red-600 dark:text-red-400"
                      role="alert"
                    >
                      {{ revokeError }}
                    </p>
                  </div>
                </div>
                <div class="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                  <button
                    type="button"
                    :disabled="revoking"
                    class="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-red-500 disabled:cursor-wait disabled:opacity-50 sm:w-auto dark:bg-red-500 dark:hover:bg-red-400 cursor-pointer"
                    @click="confirmRevoke"
                  >
                    {{ revoking ? 'Revoking…' : 'Revoke token' }}
                  </button>
                  <button
                    ref="cancelRevokeButton"
                    type="button"
                    :disabled="revoking"
                    class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto dark:bg-white/10 dark:text-white dark:inset-ring-white/5 dark:hover:bg-white/20 cursor-pointer"
                    @click="cancelRevoke"
                  >
                    Cancel
                  </button>
                </div>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </TransitionRoot>
  </section>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { onBeforeRouteLeave } from 'vue-router';
import { Dialog, DialogPanel, DialogTitle, TransitionChild, TransitionRoot } from '@headlessui/vue';
import {
  CheckIcon,
  ChevronDownIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
  CommandLineIcon,
  ExclamationTriangleIcon,
  KeyIcon,
  PlusIcon,
} from '@heroicons/vue/24/outline';

import {
  type CreatedPersonalAccessToken,
  createPersonalAccessToken,
  listPersonalAccessTokens,
  type PersonalAccessTokenSummary,
  revokePersonalAccessToken,
} from '@/api/personal-access-tokens';
import {
  DEFAULT_EXPIRY_OPTION,
  displayPrefix,
  EXPIRY_OPTIONS,
  expiryFromOption,
  PAT_NAME_MAX_LENGTH,
  PAT_SCOPES,
  READ_ONLY_SCOPES,
  scopeInfo,
  tokenApiError,
  type TokenStatus,
  tokenStatus,
  validateTokenName,
} from '@/lib/personal-access-tokens';
import { useToastStore } from '@/stores/toast';
import { useAuthStore } from '@/stores/auth';
import EmailVerificationForm from '@/components/EmailVerificationForm.vue';

const toast = useToastStore();
const auth = useAuthStore();

const STATUS_BADGE: Record<TokenStatus, { label: string; classes: string }> = {
  active: {
    label: 'Active',
    classes:
      'bg-green-50 text-green-700 inset-ring-green-600/20 dark:bg-green-400/10 dark:text-green-400 dark:inset-ring-green-500/20',
  },
  expired: {
    label: 'Expired',
    classes:
      'bg-gray-50 text-gray-600 inset-ring-gray-500/10 dark:bg-gray-400/10 dark:text-gray-400 dark:inset-ring-gray-400/20',
  },
  revoked: {
    label: 'Revoked',
    classes:
      'bg-red-50 text-red-700 inset-ring-red-600/10 dark:bg-red-400/10 dark:text-red-400 dark:inset-ring-red-400/20',
  },
};

const tokens = ref<PersonalAccessTokenSummary[]>([]);
const loading = ref(true);
const listError = ref<string | null>(null);

const createOpen = ref(false);
const submitting = ref(false);
const form = reactive({ name: '', scopes: [] as string[], expiry: DEFAULT_EXPIRY_OPTION });
const nameError = ref<string | null>(null);
const scopesError = ref<string | null>(null);
const expiryError = ref<string | null>(null);
const createError = ref<string | null>(null);
const createNeedsVerification = ref(false);

function onEmailVerified() {
  createNeedsVerification.value = false;
  createError.value = null;
}
// Holds the plaintext token only while the reveal dialog is open.
const createdToken = ref<CreatedPersonalAccessToken | null>(null);
const copied = ref(false);
const copyFailed = ref(false);
const nameInput = ref<HTMLInputElement | null>(null);
const copyButton = ref<HTMLButtonElement | null>(null);

const revokeTarget = ref<PersonalAccessTokenSummary | null>(null);
const cancelRevokeButton = ref<HTMLButtonElement | null>(null);
const revoking = ref(false);
const revokeError = ref<string | null>(null);

const now = ref(new Date());
let disposed = false;
let listGeneration = 0;
const expiryPreview = computed(() => expiryFromOption(form.expiry, now.value));

function statusOf(token: PersonalAccessTokenSummary) {
  return tokenStatus(token, now.value);
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatDateTime(iso: string) {
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

async function loadTokens() {
  const generation = ++listGeneration;
  loading.value = true;
  listError.value = null;
  try {
    const list = await listPersonalAccessTokens();
    if (disposed || generation !== listGeneration) return;
    tokens.value = list;
    now.value = new Date();
  } catch (e) {
    if (disposed || generation !== listGeneration) return;
    listError.value = tokenApiError(e, 'load your tokens').message;
  } finally {
    if (generation === listGeneration) loading.value = false;
  }
}

// A list request started before a mutation may return a snapshot without it.
function refreshIfListPending() {
  if (loading.value) void loadTokens();
}

function resetForm() {
  form.name = '';
  form.scopes = [];
  form.expiry = DEFAULT_EXPIRY_OPTION;
  nameError.value = null;
  scopesError.value = null;
  expiryError.value = null;
  createError.value = null;
  createNeedsVerification.value = false;
}

function openCreate() {
  resetForm();
  now.value = new Date();
  createOpen.value = true;
}

function closeCreate() {
  createOpen.value = false;
  createdToken.value = null;
  copied.value = false;
  copyFailed.value = false;
}

function onCreateDialogClose() {
  // Escape and outside clicks must not discard a token the user may not have copied yet.
  if (createdToken.value || submitting.value) return;
  closeCreate();
}

async function submitCreate() {
  nameError.value = validateTokenName(form.name);
  scopesError.value = form.scopes.length ? null : 'Select at least one scope.';
  expiryError.value = null;
  createError.value = null;
  createNeedsVerification.value = false;
  if (nameError.value) {
    nameInput.value?.focus();
    return;
  }
  if (scopesError.value) return;

  submitting.value = true;
  try {
    const created = await createPersonalAccessToken({
      name: form.name.trim(),
      scopes: PAT_SCOPES.map((s) => s.value).filter((v) => form.scopes.includes(v)),
      expiresAt: expiryFromOption(form.expiry).toISOString(),
    });
    if (disposed) return;
    tokens.value = [
      {
        selector: created.selector,
        name: created.name,
        scopes: created.scopes,
        createdAt: created.createdAt,
        expiresAt: created.expiresAt,
        revokedAt: null,
        lastUsedAt: null,
      },
      ...tokens.value,
    ];
    refreshIfListPending();
    now.value = new Date();
    createdToken.value = created;
    resetForm();
    await nextTick();
    copyButton.value?.focus();
  } catch (e) {
    if (disposed) return;
    const error = tokenApiError(e, 'create the token');
    if (error.field === 'name') nameError.value = error.message;
    else if (error.field === 'scopes') scopesError.value = error.message;
    else if (error.field === 'expiresAt') expiryError.value = error.message;
    else createError.value = error.message;
    if (error.emailNotVerified) {
      createNeedsVerification.value = true;
      auth.emailVerified = false;
    }
  } finally {
    submitting.value = false;
  }
}

function selectAll(event: FocusEvent) {
  (event.target as HTMLInputElement).select();
}

async function copyToken() {
  if (!createdToken.value) return;
  copyFailed.value = false;
  try {
    await navigator.clipboard.writeText(createdToken.value.token);
    copied.value = true;
  } catch {
    copied.value = false;
    copyFailed.value = true;
  }
}

function askRevoke(token: PersonalAccessTokenSummary) {
  revokeError.value = null;
  revokeTarget.value = token;
}

function cancelRevoke() {
  if (revoking.value) return;
  revokeTarget.value = null;
}

async function confirmRevoke() {
  const target = revokeTarget.value;
  if (!target) return;
  revoking.value = true;
  revokeError.value = null;
  try {
    const updated = await revokePersonalAccessToken(target.selector);
    if (disposed) return;
    tokens.value = tokens.value.map((t) => (t.selector === updated.selector ? updated : t));
    refreshIfListPending();
    now.value = new Date();
    revokeTarget.value = null;
    toast.success(`Token “${target.name}” revoked.`);
  } catch (e) {
    if (disposed) return;
    const error = tokenApiError(e, 'revoke the token');
    if (error.status === 404) {
      revokeTarget.value = null;
      toast.error(error.message);
      void loadTokens();
    } else {
      revokeError.value = error.message;
    }
  } finally {
    revoking.value = false;
  }
}

function pendingSecretWarning(): string | null {
  if (createdToken.value) {
    return 'Your new token is still shown. If you leave, it is discarded and you cannot see it again. Leave anyway?';
  }
  if (submitting.value) {
    return 'A token is still being created. If you leave, you will not see it and must revoke it later. Leave anyway?';
  }
  return null;
}

onBeforeRouteLeave((to) => {
  // The API client redirects to Login when the session ends; a prompt cannot keep the page usable then.
  if (to.name === 'Login') return true;
  const warning = pendingSecretWarning();
  return warning ? window.confirm(warning) : true;
});

function onBeforeUnload(event: BeforeUnloadEvent) {
  if (pendingSecretWarning()) event.preventDefault();
}

onMounted(() => {
  window.addEventListener('beforeunload', onBeforeUnload);
  void loadTokens();
});
onBeforeUnmount(() => {
  disposed = true;
  window.removeEventListener('beforeunload', onBeforeUnload);
  createdToken.value = null;
});
</script>
